/**
 * TruBeauté — safe browser-storage wrapper.
 *
 * Centralizes EVERY read/write to localStorage and sessionStorage so we can:
 *   1. Fail gracefully in private browsing / quota-exceeded modes
 *   2. Audit in one file what we store (non-PII flags + anon session ids only)
 *   3. Log read/write errors silently for diagnostics without breaking UX
 *
 * SECURITY INVARIANT — never store any of the following in browser storage:
 *   - Auth/session tokens (live in httpOnly cookies set by FastAPI)
 *   - Email, name, picture, or any LLM input/output
 *   - Routine bodies in non-stealth mode (stay server-side, encrypted)
 *
 * Allowed values:
 *   - boolean-ish flags ("0"/"1") for UX preferences (stealth, intros seen)
 *   - random per-tab session ids for anonymous funnel analytics
 *   - per-device routine cache (stealth mode only — separate IndexedDB layer)
 */

const _isClient = () => typeof window !== "undefined";

const _logErr = (label, err) => {
  // Surface diagnostics in dev/preview without bothering users in prod.
  // We never include the storage *value* — only the operation + error name.
  if (process.env.NODE_ENV !== "production") {
    // eslint-disable-next-line no-console
    console.debug(`[safeStorage] ${label} unavailable:`, err?.name || err);
  }
};

const _make = (kind) => {
  const get = () => {
    if (!_isClient()) return null;
    try {
      return kind === "local" ? window.localStorage : window.sessionStorage;
    } catch (e) {
      _logErr(`${kind}Storage`, e);
      return null;
    }
  };

  return {
    getItem: (key) => {
      const s = get();
      if (!s) return null;
      try {
        return s.getItem(key);
      } catch (e) {
        _logErr(`${kind}.getItem(${key})`, e);
        return null;
      }
    },
    setItem: (key, value) => {
      const s = get();
      if (!s) return false;
      try {
        s.setItem(key, value);
        return true;
      } catch (e) {
        _logErr(`${kind}.setItem(${key})`, e);
        return false;
      }
    },
    removeItem: (key) => {
      const s = get();
      if (!s) return false;
      try {
        s.removeItem(key);
        return true;
      } catch (e) {
        _logErr(`${kind}.removeItem(${key})`, e);
        return false;
      }
    },
  };
};

export const safeLocal = _make("local");
export const safeSession = _make("session");
